import { FunctionComponent, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import styles from './dropZone.module.scss'
import { Role } from '@/types/Role'
import { useMutation } from '@apollo/client'
import { CREATE_MEDIA } from '@/modules/API/news/news'
import { FilePreview } from '../Dialog/postDialog'
import { CreateMediaResponse, MediaType } from '@/types/NewsFeed'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import Loading from '@/components/Loading/Loading'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import {
    ACCEPTED_MIME_TYPES,
    DEFAULT_IMAGE_HEIGHT,
    DEFAULT_IMAGE_WIDTH,
    FILE_LIMIT_IMAGE,
    getFileLimitByMediaType,
    getMediaTypeFromMime,
    isFileSizeValid
} from '@/modules/utils/mediaConstants'

interface DropZoneProps {
    role: Role
    daycareId: number
    title?: string
    filePreview: FilePreview[]
    addNewFiles: (file: FilePreview[]) => void
    postId?: number
    setLoadingCreateMedia: (loading: boolean) => void
}

const DropZone: FunctionComponent<DropZoneProps> = ({
    role,
    daycareId,
    title,
    filePreview,
    addNewFiles,
    postId,
    setLoadingCreateMedia
}: DropZoneProps) => {
    const { t } = useTranslation()

    const [openAlert, setOpenAlert] = useState(false)
    const [alertMessage, setAlertMessage] = useState('')
    const [createMedia, { loading: loadingCreateMedia }] = useMutation(CREATE_MEDIA)

    async function onDrop(acceptedFiles: File[]): Promise<void> {
        if (acceptedFiles.length === 0) return
        const fileType: MediaType | null = getMediaTypeFromMime(acceptedFiles[0].type)
        const fileLimit: number = fileType ? getFileLimitByMediaType(fileType) : FILE_LIMIT_IMAGE

        if (!canAddOtherMedia(acceptedFiles)) {
            setAlertMessage(t('fileNotSameType'))
            setOpenAlert(true)
            return
        }
        if (!(filePreview.length + acceptedFiles.length <= fileLimit)) {
            setAlertMessage(fileType === MediaType.IMAGE ? t('maxFilesImage') : t('maxFiles'))
            setOpenAlert(true)
            return
        }

        const newFilePreviews: FilePreview[] = []

        await Promise.all(
            acceptedFiles.map(async (file) => {
                const fileType: MediaType | null = getMediaTypeFromMime(file.type)
                if (!fileType) {
                    setAlertMessage(t('fileNotSupported'))
                    setOpenAlert(true)
                    return
                }
                if (!isFileSizeValid(file, fileType)) {
                    setAlertMessage(fileType === MediaType.VIDEO ? t('videoFileTooBig') : t('otherFileTooBig'))
                    setOpenAlert(true)
                    return
                }

                let width: number = DEFAULT_IMAGE_WIDTH
                let height: number = DEFAULT_IMAGE_HEIGHT

                if (file.type.startsWith('image')) {
                    const image: HTMLImageElement = new Image()
                    image.src = URL.createObjectURL(file)
                    await new Promise<void>((resolve) => {
                        image.onload = () => {
                            width = image.width
                            height = image.height
                            resolve()
                        }
                    })
                }

                try {
                    await createMedia({
                        variables: {
                            daycareId: daycareId,
                            file: file,
                            postId: postId ? postId : undefined,
                            width: width,
                            height: height
                        }
                    }).then((r) => {
                        const createdMedia: CreateMediaResponse = r.data.createMedia
                        if (createdMedia) {
                            newFilePreviews.push({
                                mediaId: createdMedia.id,
                                mediaType: createdMedia.mediaType,
                                file: file,
                                preview: URL.createObjectURL(file)
                            })
                        }
                    })
                } catch (error) {
                    Bugsnag.notify(error as NotifiableError)
                    setAlertMessage(t('errorCreatingMedia'))
                    setOpenAlert(true)
                }
            })
        ).then(() => {
            addNewFiles(newFilePreviews)
        })
    }

    function canAddOtherMedia(acceptedFiles: File[]): boolean {
        return filePreview.length > 0 ? getMediaTypeFromMime(acceptedFiles[0].type) === filePreview[0].mediaType : true
    }

    const { getRootProps, getInputProps } = useDropzone({
        accept: ACCEPTED_MIME_TYPES,
        maxFiles: FILE_LIMIT_IMAGE,
        onDrop
    })

    function closeAlert(): void {
        setOpenAlert(false)
    }

    useEffect(() => {
        setLoadingCreateMedia(loadingCreateMedia)
        return () => {
            if (loadingCreateMedia) setLoadingCreateMedia(false)
        }
    }, [loadingCreateMedia])

    return (
        <>
            {loadingCreateMedia && <Loading overall={true} />}
            <div className={styles.DropZoneContainer}>
                <p className={role === Role.parent ? styles.parent : styles.Staff}>{title}</p>
                <div {...getRootProps()} className={styles.DropZone}>
                    <FileUploadIcon className={styles.DropZoneImg} />
                    <input {...getInputProps()} />
                    <p className={styles.DropZoneText}>{t('dropZoneMessage')}</p>
                </div>
            </div>
            <Dialog open={openAlert} handleClose={closeAlert}>
                <DialogHeader title={t('information')} />
                <DialogBody className={styles.DialogBody}>
                    <p className={styles.DialogText}>{alertMessage}</p>
                    <ButtonTextIcon type={'close'} onClick={closeAlert} />
                </DialogBody>
            </Dialog>
        </>
    )
}

export default DropZone
